/**
 * ProfileScreen — name, region, contacts and interface language.
 * STORY-001-010 Implementation logic §4 + AC #1, #2, #12.
 *
 * - RHF form for `display_name` + `region`; submit → PATCH /me.
 * - A Language group (RU / KY / EN per ADR-028) that PATCHes /me immediately
 *   AND flips the UI locale via the ui-store, so the change is visible at once
 *   (AC #12) and persists server-side (a fresh login picks it up).
 * - "Add phone" / "Change phone" opens `PhoneVerifyDialog` (STORY-001-009);
 *   on success the profile is re-read so the new number shows.
 *
 * THE 2026-08 REBUILD
 *
 * Before: four floating cards, each with an uppercase micro-label and a
 * paragraph of grey text, and a language toggle that was `inline-flex` inside a
 * stretched column — so on desktop it rendered as three small buttons stranded
 * at the left of a full-width border box (visible in the shipped screenshot).
 *
 * After: one ruled slip with three bands — what we call you / how you sign in /
 * what language we speak. Every row states its current value, each band's stub
 * says WHY the field is asked (a first-time renovator should never have to
 * guess why an app wants their region), and the language group is a real
 * three-up control that fills its row at every width.
 *
 * Failures name themselves: a save or a language change that fails raises a
 * `notify.error` that says which one failed and offers the retry, instead of
 * the old `toast.error(t("errors.generic"))`.
 *
 * Test ids (`profile-screen`, `profile-display-name`, `profile-region`,
 * `profile-save`, `profile-email`, `profile-phone`, `profile-phone-none`,
 * `profile-add-phone`, `profile-change-phone`, `profile-language-toggle`,
 * `profile-language-*`, `profile-load-error`) are all preserved — the
 * `account-language-toggle` / `account-change-phone` e2e specs key off them.
 */
import clsx from "clsx";
import { useEffect, useState, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Avatar, Button, Field } from "@garden/ui";

import { Stagger, StaggerItem } from "@/components/motion";
import {
  Slip,
  SlipRow,
  SlipSection,
  useSeenMark,
} from "@/features/account/components/Slip";
import {
  getMe,
  patchMe,
  registerAccountI18n,
  type AccountLanguage,
  type MeProfile,
} from "@/features/account/api";
import { PhoneVerifyDialog } from "@/features/auth/PhoneVerifyDialog";
import { UI_LANGUAGES, type UiLanguage } from "@/features/auth/schemas";
import { NetworkError } from "@/lib/api-client";
import { notify } from "@/lib/notify";
import { useUiStore } from "@/lib/ui-store";

interface ProfileFormValues {
  display_name: string;
  region: string;
}

/** The failure the user actually hit — a dead connection reads differently. */
function errorDescriptionKey(err: unknown): string {
  return err instanceof NetworkError ? "errors.network" : "errors.generic";
}

/** Up-to-two-character initials for the identity avatar (decorative). */
function initialsOf(profile: MeProfile | null): string {
  const source = (profile?.display_name ?? "").trim() || (profile?.email ?? "").trim();
  if (source === "") return "";
  const parts = source.split(/\s+/).filter(Boolean);
  const letters =
    parts.length >= 2
      ? `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`
      : source.slice(0, 2);
  return letters.toUpperCase();
}

export function ProfileScreen(): ReactElement {
  registerAccountI18n();
  const { t } = useTranslation("account");
  const setUiLanguage = useUiStore((s) => s.setLanguage);

  const [profile, setProfile] = useState<MeProfile | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [languageBusy, setLanguageBusy] = useState(false);
  const { marked, mark } = useSeenMark();

  const form = useForm<ProfileFormValues>({
    defaultValues: { display_name: "", region: "" },
  });
  const { reset } = form;

  // Initial load — populate the form + current language. Runs ONCE on mount;
  // `reset` is a stable RHF callback but listing it as a dep triggers a
  // react-hooks/exhaustive-deps false positive through the nested async IIFE,
  // so the mount-once intent is encoded with an empty deps array + an explicit
  // disable (the rule's documented escape hatch). STORY-001-010.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const me = await getMe();
        if (cancelled) return;
        setProfile(me);
        reset({ display_name: me.display_name, region: me.region ?? "" });
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once load (STORY-001-010)
  }, []);

  const onSubmit = async (values: ProfileFormValues): Promise<void> => {
    try {
      const updated = await patchMe({
        display_name: values.display_name.trim(),
        region: values.region.trim() === "" ? null : values.region.trim(),
      });
      setProfile(updated);
      reset({ display_name: updated.display_name, region: updated.region ?? "" });
      mark("identity");
      notify.success(t("profile.savedToast"));
    } catch (err) {
      notify.error(t("profile.saveFailed"), {
        description: t(errorDescriptionKey(err)),
        id: "profile-save",
        action: {
          label: t("profile.retry"),
          onClick: () => void form.handleSubmit(onSubmit)(),
        },
      });
    }
  };

  const onSelectLanguage = async (lang: AccountLanguage): Promise<void> => {
    if (profile?.preferred_language === lang || languageBusy) return;
    setLanguageBusy(true);
    // Flip the UI immediately (AC #12) — optimistic; revert on failure.
    const previous = profile?.preferred_language ?? "ru";
    void setUiLanguage(lang as UiLanguage);
    try {
      const updated = await patchMe({ preferred_language: lang });
      setProfile(updated);
      mark("language");
    } catch (err) {
      void setUiLanguage(previous as UiLanguage);
      notify.error(t("profile.languageFailed"), {
        description: t(errorDescriptionKey(err)),
        id: "profile-language",
        action: {
          label: t("profile.retry"),
          onClick: () => void onSelectLanguage(lang),
        },
      });
    } finally {
      setLanguageBusy(false);
    }
  };

  const isSubmitting = form.formState.isSubmitting;

  if (loadError) {
    return (
      <Slip>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-5 md:px-5">
          <p
            role="alert"
            className="text-[13.5px] text-[var(--g-error)]"
            data-testid="profile-load-error"
          >
            {t("profile.loadError")}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
          >
            {t("profile.retry")}
          </Button>
        </div>
      </Slip>
    );
  }

  const initials = initialsOf(profile);
  const hasPhone = profile?.phone_e164 != null && profile.phone_e164 !== "";

  return (
    <Stagger className="flex flex-col gap-6" data-testid="profile-screen">
      <StaggerItem>
        <Slip>
          {/* ── What we call you ───────────────────────────────────────── */}
          <SlipSection
            caption={t("profile.identityHeading")}
            note={t("profile.identityWhy")}
            headingAs="h2"
          >
            <SlipRow
              label={profile?.display_name ?? ""}
              hint={profile?.email ?? ""}
              marked={marked === "identity"}
              leading={
                <Avatar initials={initials} color="var(--g-primary)" size={44} />
              }
            />
            <div className="px-4 py-4 md:px-5">
              <form
                className="flex flex-col gap-4"
                onSubmit={form.handleSubmit(onSubmit)}
                noValidate
                aria-busy={isSubmitting}
              >
                <Field
                  id="profile-display-name"
                  label={t("profile.displayNameLabel")}
                  autoComplete="name"
                  placeholder={t("profile.displayNamePlaceholder")}
                  disabled={isSubmitting}
                  data-testid="profile-display-name"
                  {...form.register("display_name")}
                />
                <div>
                  <Field
                    id="profile-region"
                    label={t("profile.regionLabel")}
                    placeholder={t("profile.regionPlaceholder")}
                    disabled={isSubmitting}
                    aria-describedby="profile-region-why"
                    data-testid="profile-region"
                    {...form.register("region")}
                  />
                  {/* Rendered here, not via `Field`'s `hint`: that helper is
                      styled at `--g-ink-3` (38% ink), which measures ~2.4:1 on
                      white and fails WCAG 1.4.3. `--g-ink-2` clears 4.5:1, and
                      `aria-describedby` keeps the reason announced with the
                      field. */}
                  <p
                    id="profile-region-why"
                    className="mt-1.5 text-[12.5px] leading-snug text-[var(--g-ink-2)]"
                  >
                    {t("profile.regionHint")}
                  </p>
                </div>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    loading={isSubmitting}
                    data-testid="profile-save"
                  >
                    {isSubmitting ? t("profile.saving") : t("profile.save")}
                  </Button>
                </div>
              </form>
            </div>
          </SlipSection>

          {/* ── How you sign in ────────────────────────────────────────── */}
          <SlipSection
            caption={t("profile.signInHeading")}
            note={t("profile.signInWhy")}
            headingAs="h2"
          >
            <SlipRow
              label={t("profile.emailLabel")}
              hint={t("profile.emailImmutable")}
              value={profile?.email ?? ""}
              valueTestId="profile-email"
            />
            <SlipRow
              label={t("profile.phoneLabel")}
              hint={t("profile.phoneWhy")}
              value={
                hasPhone ? (
                  <span data-testid="profile-phone">{profile?.phone_e164}</span>
                ) : (
                  <span data-testid="profile-phone-none">{t("profile.phoneNone")}</span>
                )
              }
              valueAsData={hasPhone}
              trailing={
                // SCRUM-125: the "already has a phone" branch used to render a
                // bare read-only line, so a user who signed up with the wrong
                // number had no way to correct it. Both branches offer the same
                // control; only the label and the testid differ.
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPhoneOpen(true)}
                  data-testid={hasPhone ? "profile-change-phone" : "profile-add-phone"}
                >
                  {hasPhone ? t("profile.changePhone") : t("profile.addPhone")}
                </Button>
              }
            />
          </SlipSection>

          {/* ── What language we speak ─────────────────────────────────── */}
          <SlipSection
            caption={t("profile.languageLabel")}
            note={t("profile.languageHint")}
            headingAs="h2"
          >
            <SlipRow
              label={t("profile.languageRowLabel")}
              value={
                profile?.preferred_language != null
                  ? t(`profile.languageNames.${profile.preferred_language}`)
                  : undefined
              }
              marked={marked === "language"}
            >
              <div
                className="grid grid-cols-3 overflow-hidden rounded-[var(--g-radius)] border border-[var(--g-rule)]"
                role="group"
                aria-label={t("profile.languageLabel")}
                aria-busy={languageBusy}
                data-testid="profile-language-toggle"
              >
                {UI_LANGUAGES.map((lang, index) => {
                  const active = profile?.preferred_language === lang;
                  return (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => void onSelectLanguage(lang)}
                      disabled={languageBusy}
                      aria-pressed={active}
                      data-testid={"profile-language-" + lang}
                      className={clsx(
                        "min-h-11 px-2 py-2.5 text-[13.5px] transition-colors disabled:opacity-60",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                        index > 0 && "border-l border-[var(--g-rule)]",
                        active
                          ? "bg-primary font-bold text-[var(--g-on-primary)]"
                          : "bg-[var(--g-surface)] font-medium text-[var(--g-ink-2)] hover:bg-[var(--g-sunken)] hover:text-foreground",
                      )}
                    >
                      {t(`profile.languageNames.${lang}`)}
                    </button>
                  );
                })}
              </div>
            </SlipRow>
          </SlipSection>
        </Slip>
      </StaggerItem>

      {profile != null ? (
        <PhoneVerifyDialog
          open={phoneOpen}
          onClose={() => setPhoneOpen(false)}
          userId={profile.id}
          onSuccess={() => {
            // Refresh the profile so the new phone shows.
            void getMe().then(setProfile).catch(() => undefined);
          }}
        />
      ) : null}
    </Stagger>
  );
}

export default ProfileScreen;
